/*
 * SchedulePage
 *
 * Page that displays classes offered and the weekly schedule. Responsibilities:
 * - Fetches class data from '/classes/' and renders a grid of 'ClassCard'
 *   components under "Our Classes".
 * - Renders the visual schedule component ('Schedule') under "Our Schedule".
 * - Supports scrolling to anchors when the URL contains a hash (e.g.
 *   /schedule#our-schedule).
 */

import { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Box, Skeleton
} from '@mui/material';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SectionCard from '../components/SectionCard';
import ClassCard from '../components/ClassCard';
import Schedule from '../components/Schedule';
import ScrollSection from '../components/ScrollSection';
import { useLocation } from 'react-router-dom';

import api from '../api';
import { Helmet } from 'react-helmet';



function SchedulePage() {
  const location = useLocation();

  // Support scrolling to a specific section when /schedule#our-schedule
  // or /schedule#classes-offered is visited directly.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [location]);

      const [classes, setClasses] = useState([]);
      const [loading, setLoading] = useState(true);
    
      useEffect(() => {
        api.get('/classes/')
          .then((res) => {
            const data = res && res.data ? res.data : [];
            console.log(data);
            setClasses(data);
            setLoading(false);
          })
          .catch(() => {
            setClasses([]);
            setLoading(false);
          });
      }, []);

    
  return (
    <Container maxWidth="xl" sx={{ backgroundColor: '#111', color: '#fff', py: 6 }}>
      <Helmet>
        <title>Schedule — Kingdom MMA and Fitness</title>
        <meta name="description" content="Find class times for Muay Thai Kickboxing, BJJ, boxing, wrestling and MMA at Kingdom MMA. Check our weekly schedule and start a 3‑day trial." />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/schedule" />
        <meta property="og:title" content="Schedule — Kingdom MMA and Fitness" />
        <meta property="og:description" content="Class schedule and descriptions for Kingdom MMA and Fitness." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/schedule" />
  <meta property="og:image" content="https://www.kingdommixedmartialarts.ca/logo-no-text.svg" />
      </Helmet>
      {/* Page title */}
      <Typography variant="h3" align="left" sx={{ mb: 5, fontWeight: 'bold' }}>
        Classes and Schedule
      </Typography>

      {/* Section: Classes */}
      <ScrollSection id ="classes-offered">
        <SectionCard title="Our Classes" icon={<SportsMartialArtsIcon />} invert={false}>
          <Typography sx={{ mb: 3 }}>
            Whether you'd like to compete, learn to defend yourself, pick up a new hobby or simply lose a few pounds, we have the class for you. From professional athletes to first timers, everyone is welcome. We offer a 3-Day Free Trial so you can see what we're all about!
          </Typography>
          {/* Grid of ClassCard components */}
          <Grid container spacing={4}>
            {loading ? (
              // Show skeleton loaders while loading
              Array.from({ length: 4 }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx} sx={{ display: 'flex' }}>
                  <Box sx={{ width: '100%' }}>
                    <Skeleton variant="rectangular" width="100%" height={320} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.1)', mt: 2, fontSize: '1.5rem' }} />
                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.1)', fontSize: '1rem' }} />
                  </Box>
                </Grid>
              ))
            ) : (
              classes.map((classItem, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx} sx={{ display: 'flex' }}>
                  <ClassCard classItem={classItem} delay={idx * 0.1} isFirst={idx === 0} />
                </Grid>
              ))
            )}
          </Grid>
        </SectionCard>
      </ScrollSection>

    <ScrollSection id="our-schedule">
      <SectionCard
        title="Our Schedule"
        icon={<CalendarMonthIcon />}
        cardSx={{ px: { xs: 1, md: 3 } }}
      >
      
          <Typography sx={{ mb: 2 }}>
          With our memberships you have access to unlimited classes within your experience level. We offer classes for all ages and skill levels, from beginners to advanced competitors. Our schedule is designed to accommodate your busy life, with classes available throughout the week.
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
          {[
              'Beginner classes: Any experience level is welcome',
              'Advanced classes: By tryouts (held every 4 months)',
              'Fight team: By tryouts (held every 4 months)',
          ].map((item, i) => (
              <Typography key={i}>• {item}</Typography>
          ))}
          </Box>

          <Box sx={{ mx: { xs: -3, md: 0 }, minHeight: '800px' }}>
            <Schedule />
          </Box>
      </SectionCard>
    </ScrollSection>

    </Container>
  );
}

export default SchedulePage;
